import { MessageUI } from "./types";
import { useEffect, useState } from "react";
import { formatMessageTime } from "@/utils/formatMessageTime";

export function ChatMessageOut({
  message,
  funcs,
}: {
  message: MessageUI;
  funcs: any;
}) {
  /**
   * 開啟全螢幕網站預覽 modal
   * @param {string} url - 網站網址
   * @param {string} title - 標題
   */

  return (
    <>
      <div className="flex items-start space-x-2 md:space-x-3">
        <div className="w-8 h-8 md:w-10 md:h-10 bg-[#6B8E5A] rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
          <img
            src="/assets/images/logo.jpg"
            alt="麻薏 Mayi AI Logo"
            className="w-8 h-8 md:w-10 md:h-10 mx-auto bg-warm-beige rounded-xl warm-shadow"
          />
        </div>
        {renderMessageContent({ message, funcs })}
      </div>
    </>
  );
}

export function renderMessageContent({
  message,
  funcs,
}: {
  message: MessageUI;
  funcs: any;
}) {
  const metadata = message.metadata;
  const actionSlug = metadata?.action_slug;
  if (actionSlug) {
    switch (actionSlug) {
      case "cta_wait_build":
        return WaitForBuildingCard({ message });
      case "cta_line_oa":
        return LineOaContactCard({ message });
      case "cta_preview":
        return WebsitePreviewContent({ message, funcs });
      default:
        return normalMessageContent({ message });
    }
  }
  return normalMessageContent({ message });
}

export function normalMessageContent({ message }: { message: MessageUI }) {
  return (
    <>
      <div className="message-bubble bg-white border border-gray-200 rounded-2xl rounded-tl-md px-4 py-3 shadow-sm">
        <div className="text-lg md:text-base text-[#A0956B] leading-relaxed whitespace-pre-wrap">
          {message.content}
        </div>
        <div className="text-xs text-[#A0956B]/70 mt-1">
          {formatMessageTime(message.timestamp)}
        </div>
      </div>
    </>
  );
}

export function WaitForBuildingCard({ message }: { message: MessageUI }) {
  const [progress, setProgress] = useState(0);
  const [isStep3Completed, setIsStep3Completed] = useState(false);
  const [isStep4Completed, setIsStep4Completed] = useState(false);
  const [progressStatus, setProgressStatus] = useState("進行中...");

  useEffect(() => {
    if (progress < 30) {
      setProgressStatus("分析中...");
    } else if (progress < 60) {
      setProgressStatus("設計中...");
    } else if (progress < 90) {
      setProgressStatus("生成中...");
    } else if (progress < 100) {
      setProgressStatus("優化中...");
    } else {
      setIsStep3Completed(true);
      setIsStep4Completed(true);
      const status = message.lastMessage ? "即將完成" : "完成";
      setProgressStatus(status);
    }
  }, [progress]);

  const simulateProgress = () => {
    // 計算現在跟 message timestamp 的差值
    const timestampDiff =
      new Date().getTime() +
      new Date().getTimezoneOffset() * 60 * 1000 -
      message.timestamp.getTime();
    if (timestampDiff > 2 * 60 * 1000 || !message.lastMessage) {
      setProgress(100);
      return;
    } else {
      setProgress((timestampDiff / (2 * 60 * 1000)) * 100);
    }

    const progressBar = document.getElementById("progress-bar");

    const updateProgress = () => {
      setProgress(progress + Math.random() * 10);
      if (progress > 100) setProgress(100);

      // Update progress bar
      if (progressBar) {
        progressBar.style.width = progress + "%";
      }

      if (progress < 100) {
        setTimeout(updateProgress, Math.random() * 2000 + 1000);
      }
    };

    setTimeout(updateProgress, 1000);
  };

  useEffect(() => {
    simulateProgress();
  }, []);

  return (
    <>
      <div class="message-bubble max-w-md w-full">
        <div class="bg-white rounded-2xl shadow-lg border border-warm-beige overflow-hidden loading-card">
          <div class="bg-gradient-to-r from-earth-green to-soft-green p-4 md:p-6 text-white text-center">
            <div class="w-12 h-12 md:w-16 md:h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4 animate-float">
              <i class="fas fa-magic text-lg md:text-2xl text-white"></i>
            </div>
            <h3 class="text-lg md:text-xl font-semibold handwriting mb-2">
              Mayi AI 正在為您建立網站
            </h3>
            <p class="text-white/90 text-xs md:text-sm">
              運用智慧技術，量身打造專屬網站
            </p>
          </div>
          <div class="p-4 md:p-6 font-medium">
            <div class="mb-4 md:mb-6">
              <div class="flex items-center justify-between mb-2">
                <span class="text-xs md:text-sm text-tea-brown">生成進度</span>
                <span
                  id="progress-status"
                  className={`text-xs md:text-sm ${progressStatus.includes("完成") ? "text-green-600" : "text-earth-green"} font-medium`}
                >
                  {progressStatus}
                </span>
              </div>
              <div class="w-full bg-warm-beige rounded-full h-2 overflow-hidden">
                <div
                  id="progress-bar"
                  class="bg-gradient-to-r from-earth-green to-soft-green h-full rounded-full animate-progress"
                ></div>
              </div>
            </div>
            <div class="space-y-2 md:space-y-3 mb-4 md:mb-6">
              <div class="flex items-center space-x-2 md:space-x-3">
                <div class="w-5 h-5 md:w-6 md:h-6 bg-earth-green rounded-full flex items-center justify-center">
                  <i class="fas fa-check text-white text-xs"></i>
                </div>
                <span class="text-xs md:text-sm text-tea-brown">
                  分析需求與偏好
                </span>
              </div>

              <div class="flex items-center space-x-2 md:space-x-3">
                <div class="w-5 h-5 md:w-6 md:h-6 bg-earth-green rounded-full flex items-center justify-center">
                  <i class="fas fa-check text-white text-xs"></i>
                </div>
                <span class="text-xs md:text-sm text-tea-brown">
                  設計頁面結構
                </span>
              </div>

              <div className="flex items-center space-x-2 md:space-x-3">
                <div
                  className={`${isStep3Completed ? "bg-earth-green" : "bg-soft-green"}
                  w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center`}
                >
                  <i
                    className={`fas text-white text-xs ${isStep3Completed ? "fa-check " : "fa-cog animate-spin"}`}
                  />
                </div>
                <span
                  className={`text-xs md:text-sm ${isStep3Completed ? "text-tea-brown" : "text-earth-green font-medium"}`}
                >
                  生成程式碼
                </span>
              </div>

              <div className="flex items-center space-x-2 md:space-x-3">
                <div
                  className={`${isStep4Completed ? "bg-earth-green" : "bg-warm-beige"}
                  w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center`}
                >
                  <i
                    className={`fas text-white text-xs ${isStep4Completed ? "fa-check " : "fa-ellipsis-h"}`}
                  />
                </div>
                <span
                  className={`text-xs md:text-sm ${isStep4Completed ? "text-tea-brown" : "text-clay-brown/60"}`}
                >
                  優化與測試
                </span>
              </div>
            </div>
            <div class="bg-warm-beige rounded-xl p-3 md:p-4 text-center">
              <div class="flex items-center justify-center space-x-1 text-earth-green mb-2">
                <i class="fas fa-leaf text-xs md:text-sm"></i>
                <span class="text-xs md:text-sm font-medium">請耐心等待</span>
                <div class="dots-loader flex space-x-1">
                  <span class="w-1 h-1 bg-earth-green rounded-full inline-block"></span>
                  <span class="w-1 h-1 bg-earth-green rounded-full inline-block"></span>
                  <span class="w-1 h-1 bg-earth-green rounded-full inline-block"></span>
                </div>
              </div>
              <p class="text-xs text-tea-brown font-medium leading-relaxed">
                我們的 Mayi AI 正在根據您的需求，精心設計專屬的網站
                <br />
                預計需要 1-2 分鐘完成
              </p>
            </div>
          </div>
        </div>
        <div class="flex items-center space-x-1 mt-2 text-xs text-gray-500">
          <span>{formatMessageTime(message.timestamp)}</span>
        </div>
      </div>
    </>
  );
}

/**
 * 網站預覽訊息內容元件
 *
 * @param {Object} props
 * @param {MessageUI} props.message - 訊息物件，包含預覽網址與時間戳
 * @returns {JSX.Element}
 *
 * @description
 * 顯示一個網站預覽框，支援重新整理與全螢幕檢視。
 * - 使用 useRef 綁定 iframe，確保型別安全
 * - 載入時顯示 loading，iframe onLoad 後顯示內容
 * - 支援全螢幕 modal（暫維持原生 DOM 操作）
 */
export function WebsitePreviewContent({
  message,
  funcs,
}: {
  message: MessageUI;
  funcs: any;
}) {
  const [showLoading, setShowLoading] = useState(true);
  const openFullscreen = funcs.openFullscreen;

  // 預覽框參數
  const options = {
    title: message.metadata?.title || "網站預覽",
    url: message.metadata?.preview_url,
    // url: "https://mayiai.tw",
    previewId: "preview-" + message.metadata?.pp_id,
  };

  /**
   * 重新整理預覽框內容
   * @description
   * 1. 先顯示 loading（移除 loading 的 hidden）
   * 2. 隱藏 iframe（加上 iframe 的 hidden）
   * 3. 重設 iframe src 以觸發重新載入
   * 4. 等待 iframe 載入完成後，onPreviewLoad 會自動顯示 iframe 並隱藏 loading
   */
  function refreshPreview() {
    setShowLoading(true);
    setTimeout(() => {
      setShowLoading(false);
    }, 600);
  }

  // 預設載入時顯示 loading，iframe onLoad 後顯示內容
  useEffect(() => {
    // 預設顯示 loading，iframe 載入完成後自動切換
    // 可根據需求自動刷新
    refreshPreview();
  }, []);

  return (
    <>
      <div className="message-bubble max-w-lg w-full min-w-[70vw]">
        <div className="website-preview animate-fade-in">
          <div className="preview-header">
            <div className="flex items-center space-x-2">
              <i className="fas fa-globe"></i>
              <span className="font-medium text-nowrap">{options.title}</span>
              {options.url ? (
                <a
                  href={options.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/70 text-sm space-x-1"
                >
                  <span className="hidden md:inline">{options.url}</span>
                  <i className="fas fa-external-link-alt"></i>
                </a>
              ) : null}
            </div>
            <div className="preview-controls">
              <button
                className="preview-control-btn"
                onClick={() => refreshPreview()}
                title="重新整理"
                type="button"
              >
                <i className="fas fa-sync-alt"></i>
              </button>
              <button
                className="preview-control-btn"
                onClick={() => openFullscreen(options.url, options.title)}
                title="全螢幕檢視"
                type="button"
              >
                <i className="fas fa-expand"></i>
              </button>
            </div>
          </div>

          {showLoading && (
            <div
              className="preview-loading"
              id={`loading-${options.previewId}`}
            >
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-earth-green"></div>
                <span>載入網站中...</span>
              </div>
            </div>
          )}
          {!showLoading && (
            <iframe
              id={`preview-${options.previewId}`}
              className="preview-iframe h-[60vh]"
              src={options.url}
              loading="lazy"
              sandbox="allow-scripts allow-forms allow-popups"
              title={options.title}
            ></iframe>
          )}
        </div>

        <div className="flex items-center space-x-1 mt-2 text-xs text-gray-500 md:min-w-[70vw]">
          <span>{formatMessageTime(message.timestamp)}</span>
        </div>
      </div>
    </>
  );
}

export function LineOaContactCard({ message }: { message: MessageUI }) {
  const LINE_URL = "https://lin.ee/1qx88DG";
  const featureList = [
    {
      icon: "fas fa-palette",
      text: "色彩主題與品牌風格",
    },
    {
      icon: "fas fa-edit",
      text: "內容文案與圖片替換",
    },
    {
      icon: "fas fa-cogs",
      text: "功能模組增減與客製",
    },
    {
      icon: "fas fa-mobile-alt",
      text: "響應式設計優化",
    },
  ];
  const featureListHtml = featureList.map((feature, index) => (
    <div id={`feature-${index}`} class="flex items-center space-x-2">
      <div class="flex items-center justify-center min-w-5">
        <i class={feature.icon + " text-earth-green text-md"}></i>
      </div>
      <span class="font-medium text-md text-tea-brown">{feature.text}</span>
    </div>
  ));
  const serviceList = [
    "資深設計師一對一指導",
    "根據您的品牌量身定制",
    "提供專業建議和最佳實踐",
    "協助解決技術難題",
  ];
  const serviceListHtml = serviceList.map((service, index) => (
    <li key={index}>{service}</li>
  ));
  return (
    <>
      <div class="message-bubble max-w-md w-full">
        <div class="bg-white rounded-2xl shadow-lg border border-warm-beige overflow-hidden">
          <div class="bg-gradient-to-r from-earth-green to-soft-green p-4 text-white">
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <div class="w-6 h-6 sm:w-10 sm:h-10 sm:flex bg-white/20 rounded-lg flex items-center justify-center">
                  <i class="fas fa-user-tie text-lg text-white"></i>
                </div>
                <div>
                  <h3 class="text-lg md:text-xl font-bold handwriting text-nowrap">
                    需要專業微調？
                  </h3>
                  <p class="text-white/90 text-xs md:text-sm">
                    一對一專人諮詢服務
                  </p>
                </div>
              </div>
              <div class="bg-white/20 px-3 py-1 rounded-full text-xs md:text-sm font-medium">
                付費服務
              </div>
            </div>
          </div>

          <div class="p-4 md:p-6">
            <div class="mb-2">
              <p class="text-tea-brown font-medium leading-relaxed text-md md:text-base mb-4">
                雖然 Mayi AI
                已經為您建立了基礎網站，但如果您需要更精細的設計調整、功能客製化或專業建議，我們提供一對一的專人諮詢服務！
              </p>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {featureListHtml}
              </div>

              <div class="bg-warm-beige rounded-xl p-3 md:p-4 mb-4">
                <h4 class="text-md font-medium text-earth-green mb-2 flex items-center">
                  <i class="fas fa-star text-md mr-2"></i>
                  專業服務特色
                </h4>
                <ul class="text-md text-tea-brown font-medium space-y-1">
                  {serviceListHtml}
                </ul>
              </div>
            </div>

            <div class="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => window.open(LINE_URL, "_blank")}
                class="flex-1 bg-earth-green hover:bg-soft-green text-white px-4 py-2 rounded-lg font-medium transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <i class="fab fa-line text-lg"></i>
                <span>LINE 專業諮詢</span>
              </button>
            </div>
          </div>
        </div>

        <div class="flex items-center space-x-1 mt-2 text-xs text-gray-500">
          <span>{formatMessageTime(message.timestamp)}</span>
        </div>
      </div>
    </>
  );
}
